import QueryString from "query-string";
import { PlanType, ProductType } from "../reducers/models";
import Log from "./Log";
import { fetchSaleData } from "../api/shop";
import PLANS_JSON from "../constants/planMetadata";
//import { API } from "aws-amplify";
import API from "@aws-amplify/api";

export const calculateStripeTotal = (
  cost: number,
  quantity: number,
  couponInfo?: bb.model.sale.ICouponData
) => {
  // for Stripe
  if (couponInfo?.percentOff) {
    const percentDiscount = (100 - couponInfo.percentOff) / 100;

    // calculate the full bill
    let invoiceTotal = cost * quantity * 100;
    // apply coupon
    let paymentAmount = invoiceTotal * percentDiscount;
    // subtract a tiny fraction because stripe rounds 0.5 down for customers
    paymentAmount -= 0.001;
    // round again because we might have fractions of cents
    paymentAmount = Math.round(paymentAmount);

    return paymentAmount / 100;
  }
};

export const getSaleData = async () => {
  //if window url contains a coupon code
  const currentUrl = new URL(window.location.toString());
  const urlQuery = QueryString.parse(currentUrl.search);
  let promoCode =
    typeof urlQuery["promo"] === "string"
      ? urlQuery["promo"]
      : sessionStorage.getItem("promoCode");

  if (promoCode != null) {
    Log.trace(promoCode, "promo code found:");

    sessionStorage.setItem("promoCode", promoCode);
  } else {
    sessionStorage.removeItem("promoCode");
  }

  try {
    let response = await fetchSaleData(promoCode || null);
    return response;
  } catch (error:any) {
    throw new Error(error);
  }
};

export async function getHouseAdCampaign(placement: (keyof bb.state.ISale["houseAdCampaign"])):Promise<bb.model.sale.IHouseAdCampaign|null> {
  try {
    const response = await API.get("userAccountManagementURL", `/marketing/placement/${placement}`, {})
    const payload = response.payload
    let result:bb.model.sale.IHouseAdCampaign|null = null
    if (response.metadata?.status !== "SUCCESS") {
      console.error("Unexpected response status from house-ad-campaign fetch: ", response.metadata)
      result = null
    }
    else if (payload === undefined) {
      console.error("Unexpected response format from house-ad-campaign fetch: ", response)
      result = null
    }
    else if (Array.isArray(payload)) {
      // no active campaign
      result = null
    }
    else {
      result = {
        ...payload,
        startDate: new Date(Date.parse(payload.startDate)),
        endDate: new Date(Date.parse(payload.endDate)),
      }
    }
    return result
  }
  catch (e:any) {
    console.error("Got error while fetching house-ad-campaign: ", e.message)
    return null
  }
}


export const getDiscountedPlanPricing = (
  discountInfo?: bb.model.sale.ICouponData
) => {
  let discountedPlans = JSON.parse(JSON.stringify(PLANS_JSON));

  for (const [key, value] of Object.entries(PLANS_JSON)) {
    if (discountedPlans[key].name !== "Free") {
      discountedPlans[key].name = key;
      discountedPlans[key].monthlyCost = parseCost(
        value.monthlyCost,
        discountInfo
      );
      discountedPlans[key].annualCost = parseCost(
        value.annualCost,
        discountInfo
      );
      discountedPlans[key].yearCommitmentCost = parseCost(
        value.yearCommitmentCost,
        discountInfo
      );
    }
  }
  return discountedPlans;
};

export const parseCost = (cost: any, couponInfo?: bb.model.sale.ICouponData) => {
  const originalCost = parseFloat(cost);

  let fixedCost = cost;

  if (couponInfo?.percentOff) {
    // apply the discount percentage - since this is cost per unit, we pass in 1 for quantity
    fixedCost = calculateStripeTotal(cost, 1, couponInfo);
  } else if (couponInfo?.amountOff) {
    const difference = originalCost - couponInfo.amountOff;
    fixedCost = difference;
  }

  return fixedCost;
};

/**
 * Returns true if the given cart item's plan is on sale
 * given the passed in saleData
 * @param {bb.model.ISubscription} item
 * @param {bb.model.sale.ISaleData} saleData
 */
export function getPlanDiscountInfo(
  item: bb.model.ISubscription,
  saleData: bb.model.sale.ISaleData
): bb.model.sale.ICouponData | null {
  if (saleData.discountInfo === undefined) {
    return null;
  }
  let discountInfo: bb.model.sale.IPlanTypes | undefined;
  if (item.productType === ProductType.BB2) {
    discountInfo = saleData.discountInfo.BB2;
  } 
  else if (item.productType === ProductType.BB3) {
    discountInfo = saleData.discountInfo.BB3;
  }
  else if (item.productType === ProductType.BB4) {
    discountInfo = saleData.discountInfo.BB4;
  }  
  else if (item.productType === ProductType.BBBundle) {
    discountInfo = saleData.discountInfo.BBBundle
  }
  else if (item.productType === ProductType.Soundbox) {
    discountInfo = saleData.discountInfo.Soundbox
  }
  else {
    console.warn("no discount system for unknown product ", item.productType)
  }

  const itemSubId = item.planType // i.e. BB2Plus => Plus
  if (itemSubId === PlanType.Free) {
    return null
  }

  return discountInfo !== undefined && itemSubId in discountInfo
    ? (discountInfo[itemSubId] as bb.model.sale.ICouponData)
    : null;
}

export function annualToMonthlyCost(annualCost: string | number): number {
  return parseInt("" + Math.ceil(parseFloat("" + annualCost) / 12), 10);
}
